// Returns — Supabase implementation.
//
// create_return() does the whole thing in one transaction: bumps returned_qty
// per line, writes the return header and its items, puts the units back on the
// event's allocation, and flips the invoice to partially_returned / returned.
// It refuses to return more than was sold.

import { supabase } from '@/lib/supabase';
import { tsReq } from './mappers';
import type { Database } from '@/types/database';
import type { AppReturn, RefundMethod } from '@/types/domain';

type Row = Database['public']['Tables']['returns']['Row'] & {
  return_items?: Database['public']['Tables']['return_items']['Row'][] | null;
};

function mapReturn(r: Row): AppReturn {
  return {
    id: r.id,
    transactionId: r.transaction_id,
    invoiceNumber: r.invoice_number,
    eventId: r.event_id,
    items: (r.return_items ?? []).map((i) => ({
      productId: i.product_id ?? '',
      lotNumber: i.lot_number ?? undefined,
      expiryDate: i.expiry_date ? new Date(i.expiry_date).getTime() : undefined,
      unit: i.unit ?? undefined,
      name: i.name,
      qty: i.qty,
      unitPrice: i.unit_price,
      subtotal: i.subtotal,
    })),
    refundMethod: r.refund_method,
    refundAmount: r.refund_amount,
    reason: r.reason ?? undefined,
    createdBy: r.created_by ?? '',
    createdByName: r.created_by_name ?? undefined,
    createdAt: tsReq(r.created_at),
  };
}

const SELECT = '*, return_items(*)';

export async function listReturns(max = 100): Promise<AppReturn[]> {
  const { data, error } = await supabase
    .from('returns').select(SELECT).order('created_at', { ascending: false }).limit(max);
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => mapReturn(r as Row));
}

export const listAllReturns = listReturns;

export async function getReturn(id: string): Promise<AppReturn | null> {
  const { data, error } = await supabase
    .from('returns').select(SELECT).eq('id', id).maybeSingle();
  if (error) throw new Error(error.message);
  return data ? mapReturn(data as Row) : null;
}

export async function listReturnsByTransaction(transactionId: string): Promise<AppReturn[]> {
  const { data, error } = await supabase
    .from('returns').select(SELECT)
    .eq('transaction_id', transactionId)
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => mapReturn(r as Row));
}

export async function listReturnsByEvent(eventId: string | null): Promise<AppReturn[]> {
  if (!eventId) return [];
  const { data, error } = await supabase
    .from('returns').select(SELECT)
    .eq('event_id', eventId)
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => mapReturn(r as Row));
}

export interface CreateReturnItemInput {
  productId: string;
  /**
   * Which line is being returned. Required once an invoice carries the same
   * product from two lots — create_return() refuses to guess, because the
   * stock has to go back onto the shelf it came off.
   */
  allocationId?: string;
  qty: number;
}

export interface CreateReturnInput {
  transactionId: string;
  items: CreateReturnItemInput[];
  refundMethod: RefundMethod;
  reason?: string;
}

export async function createReturn(input: CreateReturnInput): Promise<{ id: string; refundAmount: number }> {
  if (input.items.length === 0) throw new Error('Pilih minimal 1 item untuk diretur');
  const { data, error } = await supabase.rpc('create_return', {
    p_transaction_id: input.transactionId,
    p_items: input.items.map((i) => ({
      product_id: i.productId,
      allocation_id: i.allocationId ?? null,
      qty: i.qty,
    })),
    p_refund_method: input.refundMethod,
    p_reason: input.reason ?? undefined,
  });
  if (error) throw new Error(error.message);
  const row = data as unknown as { id: string; refund_amount: number };
  return { id: row.id, refundAmount: row.refund_amount };
}
